import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import ProgressBar from "../../../../UI/ProgressBar";
import { useGetAdminOrgBookSummaryQuery } from "../../../../../redux-toolkit/features/reports/ReportsApiSlice";
import { Error404 } from "../../../../UI/404/Error404";
import emptyStateSearch from "../../../../../assets/employee/empty_state_search.svg";
import { useSelector } from "react-redux";
import {
  selectCurrentUser,
  selectSelectedCompany,
} from "../../../../../redux-toolkit/features/auth/authSlice";
import { resolveActiveCompanyId } from "../../../../../utils/companyContextNavigation";

const formatPercent = (value) =>
  value == null ? "0%" : `${Number(value).toFixed(1)}%`;

const COLUMN_KEYS = [
  "bookOrder",
  "bookName",
  "totalOrganisationUsers",
  "eligibleUsers",
  "completed",
  "ongoing",
  "locked",
  "completionPercent",
];

export const OrgBookSummaryTable = ({
  check,
  searchQuery = "",
  getTableData,
}) => {
  const selectedCompany = useSelector(selectSelectedCompany);
  const user = useSelector(selectCurrentUser);
  const companyId = resolveActiveCompanyId(selectedCompany, user);

  const { data, isError, isLoading } = useGetAdminOrgBookSummaryQuery(
    { query: searchQuery, companyId },
    { skip: !companyId, refetchOnMountOrArgChange: true }
  );

  React.useEffect(() => {
    if (!data) return;
    getTableData && getTableData(data);
  }, [data]);

  if (isLoading) {
    return <ProgressBar />;
  }

  if (isError) {
    return <Error404 />;
  }

  const allFieldsDeselected = Object.values(check).every((v) => !v);
  const visibleColumnCount = Object.values(check).filter(Boolean).length || 1;
  const orderedVisibleKeys = COLUMN_KEYS.filter((key) => check[key]);
  const firstVisibleKey = orderedVisibleKeys[0];
  const lastVisibleKey = orderedVisibleKeys[orderedVisibleKeys.length - 1];

  const headerCellSx = (key) => ({
    ...(key === firstVisibleKey ? { borderRadius: "20px 0px 0px 0px" } : {}),
    ...(key === lastVisibleKey ? { borderRadius: "0px 20px 0px 0px" } : {}),
  });

  return (
    <div
      style={
        allFieldsDeselected
          ? { overflow: "hidden", maxWidth: "100%", width: "100%" }
          : undefined
      }
    >
      <TableContainer
        sx={{
          height: allFieldsDeselected ? "auto" : "100%",
          maxHeight: allFieldsDeselected ? "none" : "calc(100vh - 270px)",
          padding: "0 1px",
          width: "100%",
          maxWidth: "100%",
          overflowX: allFieldsDeselected ? "hidden" : "auto",
          overflowY: allFieldsDeselected ? "hidden" : "auto",
        }}
      >
        <Table
          stickyHeader
          aria-label="org book summary table"
          className={check.bookOrder ? "reports-table--indexed reports-table--indexed-wide" : undefined}
          sx={
            allFieldsDeselected
              ? { tableLayout: "fixed", width: "100%", minWidth: 0 }
              : { minWidth: 1100 }
          }
        >
          <TableHead>
            <TableRow
              sx={{ borderRadius: "20px 20px 0px 0px" }}
              className="tableUi__head"
            >
              {allFieldsDeselected ? (
                <TableCell
                  sx={{ borderRadius: "20px 20px 0px 0px" }}
                  align="center"
                >
                  Select fields to view data
                </TableCell>
              ) : (
                <>
                  {check.bookOrder && (
                    <TableCell
                      sx={headerCellSx("bookOrder")}
                      align="center"
                    >
                      Book Order
                    </TableCell>
                  )}
                  {check.bookName && (
                    <TableCell sx={headerCellSx("bookName")} align="center">
                      Book Name
                    </TableCell>
                  )}
                  {check.totalOrganisationUsers && (
                    <TableCell
                      sx={headerCellSx("totalOrganisationUsers")}
                      align="center"
                    >
                      Total Organisation User
                    </TableCell>
                  )}
                  {check.eligibleUsers && (
                    <TableCell
                      sx={headerCellSx("eligibleUsers")}
                      align="center"
                    >
                      Eligible User
                    </TableCell>
                  )}
                  {check.completed && (
                    <TableCell sx={headerCellSx("completed")} align="center">
                      Completed
                    </TableCell>
                  )}
                  {check.ongoing && (
                    <TableCell sx={headerCellSx("ongoing")} align="center">
                      Ongoing (Current Book)
                    </TableCell>
                  )}
                  {check.locked && (
                    <TableCell sx={headerCellSx("locked")} align="center">
                      Locked (Not Yet Reached)
                    </TableCell>
                  )}
                  {check.completionPercent && (
                    <TableCell
                      sx={headerCellSx("completionPercent")}
                      align="center"
                    >
                      Completion %
                    </TableCell>
                  )}
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {allFieldsDeselected ? (
              <TableRow>
                <TableCell align="center" sx={{ border: "none", py: 6 }}>
                  Select fields to view data
                </TableCell>
              </TableRow>
            ) : searchQuery && (!data?.data || data.data.length === 0) ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  align="center"
                  sx={{ border: "none", py: 8 }}
                >
                  <img
                    src={emptyStateSearch}
                    alt="No search results"
                    style={{ width: "220px", height: "165px" }}
                  />
                </TableCell>
              </TableRow>
            ) : !data?.data?.length ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnCount}
                  align="center"
                  sx={{ border: "none", py: 8 }}
                >
                  No published organisation books found.
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((row) => (
                <React.Fragment key={row.bookId ?? `${row.bookOrder}-${row.bookName}`}>
                  <TableRow className="tableUi___body">
                    {check.bookOrder && (
                      <TableCell align="center">
                        {row.bookOrder}
                      </TableCell>
                    )}
                    {check.bookName && (
                      <TableCell align="center">
                        {row.bookName}
                      </TableCell>
                    )}
                    {check.totalOrganisationUsers && (
                      <TableCell align="center">
                        {row.totalOrganisationUsers}
                      </TableCell>
                    )}
                    {check.eligibleUsers && (
                      <TableCell align="center">
                        {row.eligibleUsers}
                      </TableCell>
                    )}
                    {check.completed && (
                      <TableCell align="center">
                        {row.completed}
                      </TableCell>
                    )}
                    {check.ongoing && (
                      <TableCell align="center">
                        {row.ongoing}
                      </TableCell>
                    )}
                    {check.locked && (
                      <TableCell align="center">
                        {row.locked}
                      </TableCell>
                    )}
                    {check.completionPercent && (
                      <TableCell align="center">
                        {formatPercent(row.completionPercent)}
                      </TableCell>
                    )}
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
